/* eslint-disable no-console */
import { publish, subscribe } from './pubsub';
import { PUB_SUB_EVENTS } from './constants';
import { HTMLUpdateUtility } from './html-update-utility';
import { enableZoomOnHover } from './magnify';
import { SectionId } from './section-id';
import ProductForm from './product-form';

interface OptionValueSelectionChangeData {
  event: Event;
  target: HTMLElement;
  selectedOptionValues: string[];
}

interface RenderProductInfoParams {
  requestUrl: string;
  targetId: string;
  callback: (html: Document) => void;
}

interface UpdateSourceFromDestinationParams {
  id: string;
  shouldHide?: (source: HTMLElement) => boolean;
}

type PreProcessCallback = (html: Document) => void;
type PostProcessCallback = (newNode: HTMLElement) => void;

class ProductInformation extends HTMLElement {
  onVariantChangeUnsubscriber: (() => void) | undefined = undefined;
  cartUpdateUnsubscriber: (() => void) | undefined = undefined;
  abortController: AbortController | undefined = undefined;
  pendingRequestUrl: string | null = null;
  preProcessHtmlCallbacks: PreProcessCallback[] = [];
  postProcessHtmlCallbacks: PostProcessCallback[] = [];
  optionChanged: boolean = false;

  constructor() {
    super();
  }

  connectedCallback(): void {
    this.initializeProductSwapUtility();

    this.onVariantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleOptionValueChange.bind(this),
    );

    this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
  }

  addPreProcessCallback(callback: PreProcessCallback): void {
    this.preProcessHtmlCallbacks.push(callback);
  }

  disconnectedCallback(): void {
    if (this.onVariantChangeUnsubscriber) {
      this.onVariantChangeUnsubscriber();
    }
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  initializeProductSwapUtility(): void {
    this.preProcessHtmlCallbacks.push((html: Document) =>
      html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel')),
    );
    this.postProcessHtmlCallbacks.push((_newNode: HTMLElement) => {
      (window as Window & { Shopify?: { PaymentButton?: { init: () => void } } })?.Shopify?.PaymentButton?.init();
      (window as Window & { ProductModel?: { loadShopifyXR: () => void } })?.ProductModel?.loadShopifyXR();
    });
  }

  handleOptionValueChange({
    data: { event, target, selectedOptionValues },
  }: {
    data: OptionValueSelectionChangeData;
  }): void {
    if (!this.contains(event.target as Node)) return;

    this.resetProductFormState();

    let focusTarget: HTMLElement | null = target;
    // if target is hidden get the label
    if (target.offsetParent === null) {
      focusTarget = document.querySelector(`[for='${target.id}']`);
    }

    const productUrl = (target as HTMLElement).dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
    this.pendingRequestUrl = productUrl;
    const shouldSwapProduct = this.dataset.url !== productUrl;
    const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

    this.renderProductInfo({
      requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
      targetId: focusTarget?.id ?? target.id,
      callback: shouldSwapProduct
        ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
        : this.handleUpdateProductInfo(productUrl),
    });
  }

  resetProductFormState(): void {
    const productForm = this.productForm;
    productForm?.toggleSubmitButton(true);
    productForm?.handleErrorMessage();
  }

  renderProductInfo({ requestUrl, targetId, callback }: RenderProductInfoParams): void {
    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.pendingRequestUrl = null;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Remove animation on succeeding variant changes
        if (this.optionChanged) {
          html.querySelector('#sticky-total-price')?.classList.remove('translate-y-full');
        }

        callback(html);
      })
      .then(() => {
        // set focus to last clicked option value
        (document.querySelector(`#${targetId}`) as HTMLElement)?.focus();
        // update variant change
        this.optionChanged = true;
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  getSelectedVariant(productInfoNode: Document): object | null {
    const selectedVariant = productInfoNode.querySelector('variant-selector [data-selected-variant]')?.innerHTML;
    return selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  buildRequestUrlWithParams(url: string, optionValues: string[], shouldFetchFullPage = false): string {
    const params: string[] = [];

    if (!shouldFetchFullPage) params.push(`section_id=${this.sectionId}`);

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }

    return `${url}?${params.join('&')}`;
  }

  updateOptionValues(html: Document): void {
    const variantSelects = html.querySelector('variant-selector');
    if (variantSelects && this.variantSelectors) {
      HTMLUpdateUtility.viewTransition(
        this.variantSelectors,
        variantSelects as HTMLElement,
        this.preProcessHtmlCallbacks,
      );
    }
  }

  handleUpdateProductInfo(productUrl: string): (html: Document) => void {
    return (html: Document) => {
      const variant = this.getSelectedVariant(html);

      this.pickupAvailability?.update(variant);
      this.updateOptionValues(html);
      this.updateURL(productUrl, variant?.id);
      this.updateVariantInputs(variant?.id);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateMedia(html, variant?.featured_media?.id);

      const updateSourceFromDestination = ({ id, shouldHide = () => false }: UpdateSourceFromDestinationParams) => {
        const source = html.getElementById(`${id}-${this.sectionId}`);
        const destination = this.querySelector(`#${id}-${this.dataset.section}`);
        if (source && destination) {
          destination.innerHTML = source.innerHTML;
          destination.classList.toggle('hidden', shouldHide(source as HTMLElement));
        }
      };

      updateSourceFromDestination({ id: 'price' });
      updateSourceFromDestination({ id: 'title' });
      updateSourceFromDestination({ id: 'mobile-title' });
      updateSourceFromDestination({ id: 'stock-level' });
      updateSourceFromDestination({ id: 'savings' });
      updateSourceFromDestination({ id: 'mobile-savings' });
      updateSourceFromDestination({ id: 'special-offer' });

      this.productForm?.toggleSubmitButton(
        html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
        (window as Window & { variantStrings?: { soldOut: string } })?.variantStrings?.soldOut,
      );

      publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.sectionId,
          html,
          variant,
        },
      });
    };
  }

  handleSwapProduct(productUrl: string, shouldFetchFullPage: boolean): (html: Document) => void {
    return (html: Document) => {
      // Update the URL to reflect the new product
      if (this.dataset.updateUrl === 'true') {
        window.history.replaceState({}, '', productUrl);
      }

      // Update the product information with the new HTML
      this.innerHTML = html.querySelector('product-information')?.innerHTML || '';

      // Re-initialize the component
      this.connectedCallback();

      // Dispatch event to notify other components
      this.dispatchEvent(
        new CustomEvent('product-info:swapped', {
          bubbles: true,
          detail: { productUrl, shouldFetchFullPage },
        }),
      );
    };
  }

  updateVariantInputs(variantId: string | undefined): void {
    this.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
    ).forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]') as HTMLInputElement;
      if (input) {
        input.value = variantId ?? '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  updateURL(url: string, variantId: string | undefined): void {
    (this.querySelector('share-button') as HTMLElement & { updateUrl: (url: string) => void })?.updateUrl(
      `${(window as Window & { shopUrl?: string })?.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`,
    );

    if (this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
  }

  setUnavailable(): void {
    this.productForm?.toggleSubmitButton(
      true,
      (window as Window & { variantStrings?: { unavailable: string } })?.variantStrings?.unavailable,
    );

    const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
      .map((id) => `#${id}-${this.dataset.section}`)
      .join(', ');
    document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
  }

  updateMedia(html: Document, variantFeaturedMediaId: string | undefined): void {
    if (!variantFeaturedMediaId) return;

    const mediaGallerySource = this.querySelector(`#MediaGallery-${this.sectionId} ul`);
    const mediaGalleryDestination = html.querySelector(`#MediaGallery-${this.sectionId} ul`);

    const refreshSourceData = () => {
      if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
      const mediaGallerySourceItems = Array.from(mediaGallerySource?.querySelectorAll('li[data-media-id]') || []);
      const sourceSet = new Set(mediaGallerySourceItems.map((item) => (item as HTMLElement).dataset.mediaId));
      const sourceMap = new Map(
        mediaGallerySourceItems.map((item, index) => [(item as HTMLElement).dataset.mediaId, { item, index }]),
      );
      return [mediaGallerySourceItems, sourceSet, sourceMap] as const;
    };

    if (mediaGallerySource && mediaGalleryDestination) {
      let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
      const mediaGalleryDestinationItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
      const destinationSet = new Set(
        mediaGalleryDestinationItems.map(({ dataset }) => (dataset as DOMStringMap).mediaId),
      );
      let shouldRefresh = false;

      // add items from new data not present in DOM
      for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
        const destinationItem = mediaGalleryDestinationItems[i];
        if (!sourceSet.has((destinationItem as HTMLElement).dataset.mediaId)) {
          mediaGallerySource.prepend(destinationItem);
          shouldRefresh = true;
        }
      }

      // remove items from DOM not present in new data
      for (let i = 0; i < mediaGallerySourceItems.length; i++) {
        const sourceItem = mediaGallerySourceItems[i];
        if (!destinationSet.has((sourceItem as HTMLElement).dataset.mediaId)) {
          sourceItem.remove();
          shouldRefresh = true;
        }
      }

      // refresh
      if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

      // if media galleries don't match, sort to match new data order
      mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
        const sourceData = sourceMap.get((destinationItem as HTMLElement).dataset.mediaId);

        if (sourceData && sourceData.index !== destinationIndex) {
          mediaGallerySource.insertBefore(
            sourceData.item,
            mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`),
          );

          // refresh source now that it has been modified
          [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
        }
      });
    }

    // set featured media as active in the media gallery
    (
      this.querySelector(`media-gallery`) as HTMLElement & { setActiveMedia?: (id: string, active: boolean) => void }
    )?.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);

    // update media modal
    const modalContent = this.productModal?.querySelector(`pdp-gallery-modal`);
    const newModalContent = html.querySelector(`pdp-gallery-modal`);
    if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
  }

  get variantSelectors(): HTMLElement | null {
    return this.querySelector('variant-selector');
  }

  get relatedProducts(): Element | null {
    const relatedProductsSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'related-products');
    return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
  }

  get sectionId(): string {
    return this.dataset.originalSection || this.dataset.section || '';
  }

  // Add missing property getters
  get productForm(): ProductForm | null {
    return this.querySelector('product-form') as ProductForm;
  }

  get pickupAvailability(): Element | null {
    return this.querySelector('pickup-availability');
  }

  get productModal(): Element | null {
    return this.querySelector('pdp-gallery-modal');
  }
}

if (!customElements.get('product-information')) {
  customElements.define('product-information', ProductInformation);
}
